import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'asana',
  version: '2.0.0',
  properties: [
    // ── Credential ──
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'asana_oauth2',
      },
      description: 'Select Asana OAuth2 credential'
    },

    // ── Resource ──
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'task',
      options: [
        { value: 'task', name: 'Task', description: 'Manage tasks' },
        { value: 'project', name: 'Project', description: 'Manage projects' },
        { value: 'subtask', name: 'Subtask', description: 'Manage subtasks' },
        { value: 'taskComment', name: 'Task Comment', description: 'Manage task comments/stories' },
        { value: 'taskProject', name: 'Task Project', description: 'Add/remove tasks from projects' },
        { value: 'taskTag', name: 'Task Tag', description: 'Add/remove tags on tasks' },
        { value: 'user', name: 'User', description: 'Get user information' }
      ]
    },

    // ── Operations per resource ──

    // Task operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getMany',
      displayOptions: { show: { resource: ['task'] } },
      options: [
        { value: 'create', name: 'Create', description: 'Create a task' },
        { value: 'delete', name: 'Delete', description: 'Delete a task' },
        { value: 'get', name: 'Get', description: 'Get a task by GID' },
        { value: 'getMany', name: 'Get Many', description: 'Get many tasks from a project' },
        { value: 'move', name: 'Move', description: 'Move a task to a section' },
        { value: 'search', name: 'Search', description: 'Search tasks in a workspace' },
        { value: 'update', name: 'Update', description: 'Update a task' }
      ]
    },
    // Project operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getMany',
      displayOptions: { show: { resource: ['project'] } },
      options: [
        { value: 'create', name: 'Create', description: 'Create a project' },
        { value: 'delete', name: 'Delete', description: 'Delete a project' },
        { value: 'get', name: 'Get', description: 'Get a project by GID' },
        { value: 'getMany', name: 'Get Many', description: 'Get many projects from a workspace' },
        { value: 'update', name: 'Update', description: 'Update a project' }
      ]
    },
    // Subtask operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getMany',
      displayOptions: { show: { resource: ['subtask'] } },
      options: [
        { value: 'create', name: 'Create', description: 'Create a subtask' },
        { value: 'getMany', name: 'Get Many', description: 'Get all subtasks of a task' }
      ]
    },
    // Task Comment operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'add',
      displayOptions: { show: { resource: ['taskComment'] } },
      options: [
        { value: 'add', name: 'Add', description: 'Add a comment to a task' },
        { value: 'remove', name: 'Remove', description: 'Remove a comment (story)' }
      ]
    },
    // Task Project operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'add',
      displayOptions: { show: { resource: ['taskProject'] } },
      options: [
        { value: 'add', name: 'Add', description: 'Add a task to a project' },
        { value: 'remove', name: 'Remove', description: 'Remove a task from a project' }
      ]
    },
    // Task Tag operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'add',
      displayOptions: { show: { resource: ['taskTag'] } },
      options: [
        { value: 'add', name: 'Add', description: 'Add a tag to a task' },
        { value: 'remove', name: 'Remove', description: 'Remove a tag from a task' }
      ]
    },
    // User operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getMany',
      displayOptions: { show: { resource: ['user'] } },
      options: [
        { value: 'get', name: 'Get', description: 'Get a user by GID' },
        { value: 'getMany', name: 'Get Many', description: 'Get all users in a workspace' }
      ]
    },

    // ── Shared fields with modeToggle (dynamic dropdowns) ──

    // Workspace selector
    {
      key: 'workspaceGid',
      label: 'Workspace',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task', 'project', 'user'],
          operation: ['create', 'getMany', 'search']
        }
      },
      description: 'The workspace to use',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getWorkspaces',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Project selector (depends on workspace)
    {
      key: 'projectGid',
      label: 'Project',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['getMany']
        }
      },
      description: 'The project to get tasks from',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getProjects',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Project selector for task:create (optional, to add task to project on creation)
    {
      key: 'projects',
      label: 'Project(s)',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['create']
        }
      },
      description: 'Project GID(s) to add the task to (comma-separated for multiple)',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getProjects',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Section selector for task:move (depends on project)
    {
      key: 'sectionGid',
      label: 'Section',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['move']
        }
      },
      description: 'The section to move the task to',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getSections',
          dependsOn: ['projectGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Project selector for task:move (to load sections from)
    {
      key: 'projectGid',
      label: 'Project (for section)',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['move']
        }
      },
      description: 'The project containing the section',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getProjects',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Workspace for task:move
    {
      key: 'workspaceGid',
      label: 'Workspace',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['move']
        }
      },
      description: 'The workspace to use',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getWorkspaces',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Team selector for project:create
    {
      key: 'teamGid',
      label: 'Team',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['create']
        }
      },
      description: 'The team to create the project in (required for organizations)',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTeams',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Assignee selector for task:create/update
    {
      key: 'assignee',
      label: 'Assignee',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['create', 'update']
        }
      },
      description: 'The user to assign the task to',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getUsers',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Tag selector for taskTag operations
    {
      key: 'tagGid',
      label: 'Tag',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['taskTag']
        }
      },
      description: 'The tag to add or remove',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTags',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Workspace for taskTag (to load tags from)
    {
      key: 'workspaceGid',
      label: 'Workspace',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['taskTag']
        }
      },
      description: 'The workspace to load tags from',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getWorkspaces',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ── Task-specific fields ──

    {
      key: 'taskGid',
      label: 'Task GID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['get', 'delete', 'update', 'move']
        }
      },
      description: 'The GID of the task'
    },
    {
      key: 'taskGid',
      label: 'Task GID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['taskComment', 'taskProject', 'taskTag'],
          operation: ['add', 'remove']
        }
      },
      description: 'The GID of the task'
    },
    {
      key: 'taskName',
      label: 'Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['create']
        }
      },
      description: 'The name of the task'
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      typeOptions: {
        rows: 4
      },
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['create', 'update']
        }
      },
      description: 'Free-form text for the task (plain text)'
    },
    {
      key: 'dueOn',
      label: 'Due Date',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['create', 'update']
        }
      },
      description: 'Due date in YYYY-MM-DD format'
    },
    {
      key: 'completed',
      label: 'Completed',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['update']
        }
      },
      description: 'Whether the task is completed'
    },
    {
      key: 'searchText',
      label: 'Search Text',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['search']
        }
      },
      description: 'Text to search for in task names and descriptions'
    },

    // Task name for update
    {
      key: 'taskName',
      label: 'Name',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['task'],
          operation: ['update']
        }
      },
      description: 'New name for the task'
    },

    // ── Project-specific fields ──

    {
      key: 'projectGid',
      label: 'Project GID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['get', 'delete', 'update']
        }
      },
      description: 'The GID of the project'
    },
    {
      key: 'projectGid',
      label: 'Project GID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['taskProject'],
          operation: ['add', 'remove']
        }
      },
      description: 'The project GID to add/remove the task from'
    },
    {
      key: 'projectName',
      label: 'Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['create']
        }
      },
      description: 'The name of the project'
    },
    {
      key: 'projectNotes',
      label: 'Notes',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['create', 'update']
        }
      },
      description: 'Free-form text for the project'
    },
    {
      key: 'projectColor',
      label: 'Color',
      type: 'options',
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['create', 'update']
        }
      },
      options: [
        { value: 'none', name: 'None' },
        { value: 'dark-pink', name: 'Dark Pink' },
        { value: 'dark-green', name: 'Dark Green' },
        { value: 'dark-blue', name: 'Dark Blue' },
        { value: 'dark-red', name: 'Dark Red' },
        { value: 'dark-teal', name: 'Dark Teal' },
        { value: 'dark-brown', name: 'Dark Brown' },
        { value: 'dark-orange', name: 'Dark Orange' },
        { value: 'dark-purple', name: 'Dark Purple' },
        { value: 'dark-warm-gray', name: 'Dark Warm Gray' },
        { value: 'light-pink', name: 'Light Pink' },
        { value: 'light-green', name: 'Light Green' },
        { value: 'light-blue', name: 'Light Blue' },
        { value: 'light-red', name: 'Light Red' },
        { value: 'light-teal', name: 'Light Teal' },
        { value: 'light-brown', name: 'Light Brown' },
        { value: 'light-orange', name: 'Light Orange' },
        { value: 'light-purple', name: 'Light Purple' },
        { value: 'light-warm-gray', name: 'Light Warm Gray' }
      ],
      description: 'The color of the project'
    },
    {
      key: 'privacySetting',
      label: 'Privacy Setting',
      type: 'options',
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['create']
        }
      },
      options: [
        { value: 'public_to_workspace', name: 'Public to Workspace' },
        { value: 'private_to_team', name: 'Private to Team' },
        { value: 'private', name: 'Private' }
      ],
      description: 'The privacy setting of the project'
    },
    // Project name for update
    {
      key: 'projectName',
      label: 'Name',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['project'],
          operation: ['update']
        }
      },
      description: 'New name for the project'
    },

    // ── Subtask-specific fields ──

    {
      key: 'parentTaskGid',
      label: 'Parent Task GID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['subtask']
        }
      },
      description: 'The GID of the parent task'
    },
    {
      key: 'subtaskName',
      label: 'Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['subtask'],
          operation: ['create']
        }
      },
      description: 'The name of the subtask'
    },
    {
      key: 'subtaskNotes',
      label: 'Notes',
      type: 'string',
      displayOptions: {
        show: {
          resource: ['subtask'],
          operation: ['create']
        }
      },
      description: 'Free-form text for the subtask'
    },

    // ── Task Comment fields ──

    {
      key: 'commentText',
      label: 'Comment Text',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['taskComment'],
          operation: ['add']
        }
      },
      description: 'The text of the comment (or HTML if HTML mode is enabled)'
    },
    {
      key: 'isHtml',
      label: 'HTML Mode',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['taskComment'],
          operation: ['add']
        }
      },
      description: 'Send the comment as HTML instead of plain text'
    },
    {
      key: 'storyGid',
      label: 'Story GID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['taskComment'],
          operation: ['remove']
        }
      },
      description: 'The GID of the story (comment) to remove'
    },

    // ── User fields ──

    {
      key: 'userGid',
      label: 'User GID',
      type: 'string',
      required: true,
      default: 'me',
      displayOptions: {
        show: {
          resource: ['user'],
          operation: ['get']
        }
      },
      description: 'The GID of the user (use "me" for the authenticated user)'
    },

    // Workspace for user:getMany
    {
      key: 'workspaceGid',
      label: 'Workspace',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['user'],
          operation: ['getMany']
        }
      },
      description: 'The workspace to get users from',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getWorkspaces',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ── Pagination controls ──

    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          operation: ['getMany', 'search']
        }
      },
      description: 'Whether to return all results or only up to a given limit'
    },
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      displayOptions: {
        show: {
          operation: ['getMany', 'search'],
          returnAll: [false]
        }
      },
      description: 'Maximum number of results to return'
    }
  ]
};

export default schema;
